import { useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { search as getEnrollments } from 'store/person/enrollment'
import ScheduleListIndex from './List'
import { Box, Select, FormControl, InputLabel, MenuItem, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { resolveCurrentMarkingPeriodId } from 'utils'

export default function SchedulePage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { personId } = useParams()
  const enrollments = useSelector(state => state.person2?.enrollments && state.person2.enrollments[+personId])

  const [selected, setSelected] = useState('')
  const [selectedMarkingPeriod, setSelectedMarkingPeriod] = useState('')

  useEffect(() => {
    if (!personId) return
    enrollments || dispatch(getEnrollments({ personId: +personId }))
  }, [dispatch, personId, enrollments])

  const options = useMemo(() => {
    const all = enrollments?.campusEnrollments ?? []
    const open = all.filter(
      campusEnrollment =>
        !campusEnrollment.endDate || !dayjs(campusEnrollment.endDate).isBefore(dayjs(), 'day'),
    )

    if (open.length) return open

    return [...all].sort((a, b) => dayjs(b.endDate ?? b.startDate).diff(dayjs(a.endDate ?? a.startDate)))
  }, [enrollments])

  useEffect(() => {
    if (!options.length) return
    if (options.some(campusEnrollment => campusEnrollment.id === selected)) return

    setSelected(options[0].id)
  }, [options, selected])

  const current = useMemo(
    () => options.find(campusEnrollment => campusEnrollment.id === selected),
    [options, selected],
  )

  const markingPeriods = useMemo(
    () => current?.campus?.academicYears?.[0]?.markingPeriods ?? [],
    [current],
  )

  useEffect(() => {
    if (!markingPeriods.length) return
    if (markingPeriods.some(markingPeriod => markingPeriod.id === selectedMarkingPeriod)) return

    setSelectedMarkingPeriod(resolveCurrentMarkingPeriodId(markingPeriods) ?? '')
  }, [markingPeriods, selectedMarkingPeriod])

  if (!enrollments) return null

  return (
    <Box>
      <Grid container>
        <Grid item xs={4} md={4} sx={{ my: 3, mr: 1, ml: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="campus" sx={{ top: '-7px' }}>
              {t('Campus')}
            </InputLabel>
            <Select
              size="small"
              labelId="campus"
              id="campuses"
              value={selected}
              label={t('Campus')}
              onChange={event => setSelected(event.target.value)}
            >
              {options.map(campusEnrollment => (
                <MenuItem key={campusEnrollment.id} value={campusEnrollment.id}>
                  {campusEnrollment.campus?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4} md={4} sx={{ my: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="markingPeriod" sx={{ top: '-7px' }}>
              {t('Marking Period')}
            </InputLabel>
            <Select
              size="small"
              label={t('Marking Period')}
              id="markingPeriods"
              value={selectedMarkingPeriod}
              onChange={event => setSelectedMarkingPeriod(event.target.value)}
            >
              {markingPeriods?.map((markingPeriod) => (
                <MenuItem key={markingPeriod.id} value={markingPeriod.id}>
                  {markingPeriod.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <ScheduleListIndex
        schoolEnrollmentId={current?.schoolEnrollmentId}
        campusId={current?.campus?.id}
        memberId={current?.personAccessView?.id}
        markingPeriodId={selectedMarkingPeriod}
      />
    </Box>
  )
}
